import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Sparkle, Warning, CheckCircle, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

function tryParseJson(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch {}
  // Try to extract JSON block
  const m = raw.match(/\{[\s\S]*\}/);
  if (m) {
    try { return JSON.parse(m[0]); } catch {}
  }
  return null;
}

export default function AIAudit() {
  const { activeBiz } = useOutletContext();
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [raw, setRaw] = useState("");

  const run = async () => {
    if (!activeBiz) return;
    setBusy(true);
    setAudit(null);
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/ai/audit`);
      setRaw(data.raw);
      const parsed = tryParseJson(data.raw);
      if (parsed) setAudit(parsed);
      else toast.error("AI returned non-JSON output. Showing raw text below.");
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-5xl">
      <header className="flex items-start justify-between">
        <div>
          <div className="label-tiny mb-1">AI Audit</div>
          <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="audit-title">Top-3 Readiness Score</h1>
          <p className="text-zinc-500 mt-1">Powered by GPT-5.2 · Analyzes the 47 ranking signals.</p>
        </div>
        <button className="btn-primary" onClick={run} disabled={busy} data-testid="audit-run">
          {busy ? <span className="spinner"></span> : <><Sparkle size={14} weight="fill" /> Run AI Audit</>}
        </button>
      </header>

      {!audit && !busy && !raw && (
        <div className="bento p-12 text-center">
          <MagnifyingGlass size={32} className="mx-auto text-zinc-400 mb-4" />
          <h3 className="font-display text-xl font-bold mb-2">Ready when you are</h3>
          <p className="text-zinc-600 max-w-md mx-auto">Click "Run AI Audit" to scan {activeBiz.name} for ranking blockers and growth levers.</p>
        </div>
      )}

      {audit && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bento p-6">
            <div className="label-tiny">Health Score</div>
            <div className="font-display text-7xl font-black mt-2 tabular-nums" data-testid="audit-score">
              {audit.health_score ?? "—"}<span className="text-zinc-400 text-3xl">/100</span>
            </div>
          </div>
          <div className="lg:col-span-2 bento p-6">
            <div className="label-tiny">Summary</div>
            <p className="mt-3 leading-relaxed font-mono text-sm" data-testid="audit-summary">{audit.summary}</p>
          </div>
          <div className="lg:col-span-3 space-y-3">
            <div className="label-tiny">Findings & Actions</div>
            {(audit.categories || []).map((c, i) => (
              <div key={i} className="bento p-5" data-testid={`finding-${i}`}>
                <div className="flex items-start gap-4">
                  <SeverityIcon sev={c.severity} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h4 className="font-display font-bold text-base">{c.title}</h4>
                      <span className={`label-tiny ${c.severity === "high" ? "text-[#EF4444]" : c.severity === "medium" ? "text-[#EAB308]" : "text-zinc-500"}`}>{c.severity}</span>
                    </div>
                    <p className="text-zinc-600 text-sm mt-1">{c.why}</p>
                    <div className="mt-3 p-3 bg-zinc-50 border-l-2 border-[#002FA7] text-sm font-mono">{c.action}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {raw && !audit && (
        <div className="bento p-6">
          <div className="label-tiny mb-3">Raw AI Output</div>
          <pre className="font-mono text-xs whitespace-pre-wrap leading-relaxed">{raw}</pre>
        </div>
      )}
    </div>
  );
}

function SeverityIcon({ sev }) {
  if (sev === "high") return <Warning size={20} className="text-[#EF4444] flex-shrink-0 mt-1" weight="fill" />;
  if (sev === "medium") return <Warning size={20} className="text-[#EAB308] flex-shrink-0 mt-1" />;
  return <CheckCircle size={20} className="text-[#22C55E] flex-shrink-0 mt-1" />;
}
